import { useMemo } from "react";
import { usePublishNode } from "../../pages/devices/hooks/usePublishNode";
import { useStream } from "../hooks/useStream";
import {
  errorNotification,
  successNotification,
} from "../variables/notification";

type ModelActionMessage = {
  TARGET: "MODEL";
  ACTION: "START" | "STOP";
  model_name: string;
  instance_name: string;
  DATA?: {
    serviceType: string;
  };
};

type SaveImageMessage = {
  TARGET: "CAMERA_MANAGER";
  ACTION: "SAVE_IMAGE";
  device_id: string;
  s3_key?: string | null;
  footer?: "TRUE" | "FALSE";
  ACK?: string | null;
};

type KinesisStreamMessage = {
  TARGET: "KINESIS_STREAM";
  ACTION: `KINESIS_STREAM_${"START" | "END"}`;
  streamName: string;
  deviceName: string;
};

type PanTiltMessage = {
  TARGET: "PANTILT";
  ACTION: `${"START_PT_CONTROL" | "STOP_PT_CONTROL"}`;
  device_name: string;
};

type SetFocusMessage = {
  TARGET: "CAMERA_MANAGER";
  ACTION: "SET_FOCUS";
  device_id: string;
  focus_mode: "auto" | "manual";
  focus_rect?: { x1: number; y1: number; x2: number; y2: number };
  focus_distance?: number;
};

export type MessageType =
  | ModelActionMessage
  | SaveImageMessage
  | KinesisStreamMessage
  | PanTiltMessage
  | SetFocusMessage;

interface IModelActionParameters {
  modelName: string;
  instanceName: string;
  nodeId: string;
  successMessage?: string;
  errorMessage?: string;
}

interface IKinesisStreamParameters {
  action: "START" | "END";
  deviceName: string;
  deviceId: string;
  nodeId: string;
  successMessage?: string;
  errorMessage?: string;
}

interface IPanTiltParameters {
  action: "START" | "STOP";
  deviceName: string;
  nodeId: string;
  successMessage?: string;
  errorMessage?: string;
}

interface ISetFocusParameters {
  deviceId: string;
  nodeId: string;
  focusMode: "auto" | "manual";
  focusRect?: { x1: number; y1: number; x2: number; y2: number };
  focusDistance?: number;
  successMessage?: string;
  errorMessage?: string;
}

interface ISaveImageParameters {
  deviceId: string;
  nodeId: string;
  s3Key?: string | null;
  footer?: "TRUE" | "FALSE";
  ack?: string | null;
  successMessage?: string;
  errorMessage?: string;
}

type FetchRequestOptions = {
  successMessage?: string;
  errorMessage?: string;
};

type PublishNodeFunction = ReturnType<typeof usePublishNode>["publishNode"];

const buildMqttPublishHelpers = (publishNode: PublishNodeFunction) => {
  const { getStreamName } = useStream();

  const fetchRequest = (
    message: MessageType,
    nodeId: string,
    options: FetchRequestOptions = {}
  ): Promise<void> =>
    publishNode({
      message: JSON.stringify(message),
      nodeId,
    })
      .then((response): void => {
        if (response.data && options.successMessage) {
          successNotification(options.successMessage);
        }
      })
      .catch((error): void => {
        errorNotification(
          options.errorMessage ?? "Something went wrong when publishing node"
        );

        console.error(error);

        throw error;
      });

  const startModelAction = ({
    modelName,
    instanceName,
    nodeId,
    successMessage,
    errorMessage,
  }: IModelActionParameters) =>
    fetchRequest(
      {
        TARGET: "MODEL",
        ACTION: "START",
        model_name: modelName,
        instance_name: instanceName,
        DATA: {
          serviceType: modelName,
        },
      },
      nodeId,
      {
        successMessage,
        errorMessage,
      }
    );

  function kinesisStreamMessage({
    action, // START or END
    deviceName,
    deviceId,
    nodeId,
    successMessage,
    errorMessage,
  }: IKinesisStreamParameters): Promise<void> {
    const streamName = getStreamName(nodeId, deviceId);

    return fetchRequest(
      {
        TARGET: "KINESIS_STREAM",
        ACTION: `KINESIS_STREAM_${action}`,
        streamName,
        deviceName,
      },
      nodeId,
      {
        successMessage,
        errorMessage,
      }
    );
  }

  function panTiltMessage({
    action, // START or STOP
    deviceName,
    nodeId,
    successMessage,
    errorMessage,
  }: IPanTiltParameters): Promise<void> {
    return fetchRequest(
      {
        TARGET: "PANTILT",
        ACTION: `${action}_PT_CONTROL`,
        device_name: deviceName,
      },
      nodeId,
      {
        successMessage,
        errorMessage,
      }
    );
  }

  const stopModelAction = ({
    modelName,
    instanceName,
    nodeId,
    successMessage,
    errorMessage,
  }: IModelActionParameters) =>
    fetchRequest(
      {
        TARGET: "MODEL",
        ACTION: "STOP",
        model_name: modelName,
        instance_name: instanceName,
        DATA: {
          serviceType: modelName,
        },
      },
      nodeId,
      {
        successMessage,
        errorMessage,
      }
    );

  const requestDeviceImage = ({
    deviceId,
    nodeId,
    s3Key,
    footer,
    ack,
    successMessage,
    errorMessage,
  }: ISaveImageParameters) => {
    const message: SaveImageMessage = {
      TARGET: "CAMERA_MANAGER",
      ACTION: "SAVE_IMAGE",
      device_id: deviceId,
    };

    if (s3Key !== undefined) {
      message.s3_key = s3Key;
    }

    if (footer) {
      message.footer = footer;
    }

    if (ack) {
      message.ACK = ack;
    }

    return fetchRequest(message, nodeId, {
      successMessage,
      errorMessage,
    });
  };

  const setFocus = ({
    deviceId,
    nodeId,
    focusMode,
    focusRect,
    focusDistance,
    successMessage,
    errorMessage,
  }: ISetFocusParameters) => {
    const message: SetFocusMessage = {
      TARGET: "CAMERA_MANAGER",
      ACTION: "SET_FOCUS",
      device_id: deviceId,
      focus_mode: focusMode,
    };

    if (focusRect) message.focus_rect = focusRect;

    if (focusDistance !== undefined) message.focus_distance = focusDistance;

    return fetchRequest(message, nodeId, { successMessage, errorMessage });
  };

  return {
    startModelAction,
    stopModelAction,
    requestDeviceImage,
    kinesisStreamMessage,
    panTiltMessage,
    setFocus,
  };
};

const mqttPublish = () => {
  const { publishNode, loading } = usePublishNode();

  const actions = useMemo(
    () => buildMqttPublishHelpers(publishNode),
    [publishNode]
  );

  return {
    ...actions,
    loading,
  };
};

export { buildMqttPublishHelpers };

export default mqttPublish;
